import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import useCalendarStyles from './useCalendarStyles';
import { getMonthNames, getWeekDayNames } from '@application/utilities/calendarHelper';
import i18n from '@application/utilities/i18n';
const StyledCalendar = ({ selectedDates = [], highlightedDates = [], onDateClick, onMonthChange, events = [], eventColors = {}, }) => {
    const styles = useCalendarStyles();
    // const [currentDate, setCurrentDate] = useState<Date>(() => {
    //   const now = new Date();
    //   return new Date(now.getFullYear(), now.getMonth(), 1);
    // });
    const [currentDate, setCurrentDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const locale = i18n?.language || 'en-US';
    const monthNames = useMemo(() => getMonthNames(locale, 'long'), [locale]);
    const dayNames = useMemo(() => getWeekDayNames(locale, 'narrow'), [locale]);
    const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    const currentYear = new Date().getFullYear();
    const isCurrentYear = currentDate.getFullYear() === currentYear;
    const isJanuary = currentDate.getMonth() === 0; // January is month 0
    const isDecember = currentDate.getMonth() === 11; // December is month 11
    const handlePrevMonth = () => {
        // Prevent navigation outside current year
        if (isCurrentYear && isJanuary) {
            return; // Already at January of current year
        }
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        // If navigating would go to previous year, don't allow it
        if (newDate.getFullYear() < currentYear) {
            return;
        }
        setCurrentDate(newDate);
        onMonthChange?.(newDate.getMonth() + 1, newDate.getFullYear());
    };
    const handleNextMonth = () => {
        // Prevent navigation outside current year
        if (isCurrentYear && isDecember) {
            return; // Already at December of current year
        }
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
        // If navigating would go to next year, don't allow it
        if (newDate.getFullYear() > currentYear) {
            return;
        }
        setCurrentDate(newDate);
        onMonthChange?.(newDate.getMonth() + 1, newDate.getFullYear());
    };
    const handleDateClick = (day) => {
        const clicked = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        onDateClick?.(clicked);
    };
    const renderCalendarDays = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDay = getFirstDayOfMonth(currentDate);
        const nodes = [];
        for (let i = 0; i < firstDay; i++) {
            const prevMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), -firstDay + i + 1).getDate();
            nodes.push(_jsx(Box, { sx: styles.dayCell, "aria-hidden": true, children: _jsx(Typography, { variant: 'body2', sx: styles.emptyDay, children: prevMonthDate }) }, `empty-${i}`));
        }
        // Actual days for current visible month (clickable)
        for (let day = 1; day <= daysInMonth; day++) {
            const isSelected = selectedDates.includes(day);
            const isHighlighted = highlightedDates.includes(day);
            const dayEvents = events.filter((e) => e.date === day);
            const eventDots = (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', gap: '2px', mt: '2px' }, children: dayEvents.map((e, i) => (_jsx(Box, { sx: {
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: eventColors[e.type] || 'gray'
                    } }, `${e.type}-${i}`))) }));
            const combinedCellSx = {
                ...styles.dayCell,
                cursor: 'pointer'
            };
            const combinedTextSx = {
                ...styles.dayText,
                ...(isSelected ? styles.selectedDayText : {}),
                ...(isHighlighted ? styles.highlightedDayText : {})
            };
            nodes.push(_jsxs(Box, { sx: combinedCellSx, role: 'button', tabIndex: 0, onClick: () => handleDateClick(day), onKeyDown: (ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        handleDateClick(day);
                    }
                }, "aria-label": `Select ${day} ${monthNames[currentDate.getMonth()]}`, children: [_jsx(Typography, { variant: 'body2', sx: combinedTextSx, children: day }), eventDots] }, day));
        }
        return nodes;
    };
    return (_jsxs(Box, { sx: styles.container, children: [_jsxs(Box, { sx: styles.header, children: [_jsx(Typography, { variant: 'subtitle1', sx: styles.monthTitle, children: monthNames[currentDate.getMonth()] }), _jsxs(Box, { sx: styles.navGroup, children: [_jsx(IconButton, { onClick: handlePrevMonth, sx: styles.navButton, "aria-label": 'Previous month', disabled: isCurrentYear && isJanuary, children: _jsx(ChevronLeft, { sx: styles.navIcon }) }), _jsx(IconButton, { onClick: handleNextMonth, sx: styles.navButton, "aria-label": 'Next month', disabled: isCurrentYear && isDecember, children: _jsx(ChevronRight, { sx: styles.navIcon }) })] })] }), _jsx(Box, { sx: styles.dayHeaders, children: dayNames.map((day, index) => (_jsx(Box, { sx: styles.dayHeaderCell, children: _jsx(Typography, { variant: 'caption', sx: styles.dayHeaderText, children: day }) }, `${day}-${index}`))) }), _jsx(Box, { sx: styles.calendarGrid, children: renderCalendarDays() })] }));
};
export default StyledCalendar;
